/**
 * Who Alfie should talk to, when a surface on screen knows better than the global widget.
 *
 * THE PROBLEM (U-2, open since slice 7). The oracle asked for "pressing ask-Alfie opens the Alfie
 * panel with the account as context". It cannot: the widget's only programmatic seam is a DOM
 * event whose listener is `() => open('event')`, which discards any payload and has no dispatchers
 * anywhere in the repo. So the mapping flow grew its own panel — and then pressing the disc, two
 * inches away, opened a SECOND panel in the same corner that knew nothing about the row you were
 * standing on.
 *
 * THE SEAM. A surface that can answer better than the widget CLAIMS the launcher while it is up,
 * and releases it when it goes. The widget asks before opening itself; if the claim is held, it
 * hands the press over instead. Nothing about the widget's own behaviour changes when no claim
 * exists, which is every other screen in the app.
 *
 * MODULE STATE, NOT CONTEXT, and deliberately. The claimant (`SetupOverlay`) and the asker
 * (`AlfieWidget`) are mounted in different trees — the overlay portals to `document.body` — so no
 * provider encloses both. A module-level claim is the smallest thing that spans them, and the
 * single-claim rule means there is never a question of which one wins.
 *
 * ALWAYS RELEASE IN A CLEANUP. A claim outliving its surface leaves the disc pressing into a
 * closure that renders nothing — the disc would look alive and do nothing at all. `claimLauncher`
 * returns its own release function so the caller cannot forget which to call.
 */

type Claimant = { id: string; open: () => void };

let current: Claimant | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

/**
 * Claim the launcher. Returns the release, which is safe to call twice and will not release a
 * claim some other surface has taken in the meantime — a late cleanup from an unmounting screen
 * must never silence the one that replaced it.
 */
export function claimLauncher(id: string, open: () => void): () => void {
  current = { id, open };
  notify();
  return () => {
    if (current?.id === id) {
      current = null;
      notify();
    }
  };
}

/** True when some surface will handle the press itself. */
export const launcherIsClaimed = (): boolean => current !== null;

/**
 * Hand the press to the claimant. Returns false when nobody holds it, so the widget can fall
 * through to its normal behaviour rather than swallowing the click.
 */
export function openClaimant(): boolean {
  if (!current) return false;
  current.open();
  return true;
}

/** Subscribe to claim changes, so the disc can re-label itself while one is held. */
export function subscribeClaim(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Tests only: drop any claim so one case cannot leak into the next. */
export function __resetClaim(): void {
  current = null;
  listeners.clear();
}
